/**
 * The grant page's data access.
 *
 * One read (`/authorize/preview`) and one write (`/authorize/approve`). The
 * page used to open with five parallel calls plus a fetch of up to ten thousand
 * nodes; consolidating that is the whole point of the preview endpoint, so this
 * domain stays deliberately small — a second read here would be a regression.
 */
export default {
  /**
   * Everything the page renders, in one answer.
   *
   * Name exactly one counterparty: clientId | userId | toGroupId | followId |
   * requestId | isPublic. The rest are qualifiers on that counterparty —
   * groupId is who you are ACTING AS, nodeId narrows a person-share to one
   * node, editAuthId rehydrates an existing grant.
   *
   * Every one of these must be forwarded. The four that were missing were all
   * supported by the server, so the links that used them reached the endpoint
   * naming NO counterparty and came back 400.
   */
  async getGrantPreview({
    clientId, userId, toGroupId, isPublic, groupId, subEntityKey,
    followId, requestId, nodeId, editAuthId, shareToRealm, realmHint, invitationId,
    shareToGroup, groupHint, redirectUri,
  } = {}) {
    // DEV ONLY — `/grant?fixture=<key>` renders an edge from the backend's own
    // preview fixtures instead of calling the network, so every counterparty
    // can be opened locally without a login or a seeded graph. Dynamic import,
    // behind import.meta.env.DEV, so neither this branch nor the fixtures
    // reach a production bundle. Delete these five lines and
    // `pages/grant/devFixtures.js` to remove it.
    if (import.meta.env?.DEV && typeof window !== 'undefined') {
      const key = new URLSearchParams(window.location.search).get('fixture');
      if (key) return (await import('../../pages/grant/devFixtures')).devPreview(key);
    }
    const params = {
      client_id: clientId,
      user_id: userId,
      to_group_id: toGroupId,
      public: isPublic ? 'true' : undefined,
      group_id: groupId,
      sub_entity_key: subEntityKey,
      follow_id: followId,
      request_id: requestId,
      node_id: nodeId,
      edit_auth_id: editAuthId,
      // Filing into a realm is its own counterparty; without this the server
      // sees a bare node_id and answers "node_id is only meaningful with
      // user_id" — the third time a supported parameter was read off the URL
      // and then dropped before the call.
      share_to_realm: shareToRealm ? 'true' : undefined,
      realm_hint: realmHint,
      // Sharing with a group is its own counterparty too — same trap as above, and
      // `group_hint` is NOT `group_id`: that one means "acting for this group".
      share_to_group: shareToGroup ? 'true' : undefined,
      group_hint: groupHint,
      // the invitation being answered, so a join page can say what it offers
      invitation_id: invitationId,
      // The address to vet. `return_to` in the answer is the only place the page
      // will send a person, so dropping this strands them on Nucleus after every
      // approve, and the app never gets its session code.
      redirect_uri: redirectUri,
    };
    const q = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v != null && v !== ''),
    ).toString();
    return this.request(`/authorize/preview?${q}`);
  },

  // NOTE: `getProposal` is NOT redefined here. schemas.js already has it, with
  // the same implementation — and because this domain merges last, a copy here
  // silently shadowed that one. Two names, one endpoint, no way to tell which
  // ran. Ratification uses the existing method.

  /** Approve then apply. Two calls because they are two acts: the second is the
   *  one that touches the graph, and a client that only approves has changed
   *  nothing. */
  async ratifyProposal(id) {
    await this.request(`/proposals/${id}/approve`, { method: 'POST', body: {} });
    return this.request(`/proposals/${id}/apply`, { method: 'POST', body: {} });
  },

  /** Ask a plugin to run.
   *
   *  A run is not something the consent page can conjure: the SERVER decides
   *  whether consent is needed and hands back a consent_url to follow. */
  async runPlugin(pluginId, { toolId, inputs } = {}) {
    return this.request(`/plugins/${pluginId}/execute`, {
      method: 'POST',
      body: {
        ...(toolId ? { tool_id: toolId } : {}),
        ...(inputs ? { input_parameters: inputs } : {}),
      },
    });
  },
};
